/**
 * The mail outbox's contract — plan §2 U65.
 *
 * `apps/api` and `apps/worker` both write `mail_outbox` rows and only the worker sends them,
 * so each template's name, stream and payload is spelled here once. A template's stream is
 * fixed by its name, so no caller chooses one — Phase 4 plan QP5: a safety message never
 * shares sender reputation with anything else.
 */
import { z } from "zod";
import { GTIN14 } from "./api/patterns.js";
import { INVITE_TOKEN } from "./api/team.js";
import { ROLES } from "./roles.js";
import { TIERS } from "./tiers.js";
import { USAGE_WINDOW } from "./usage.js";
/** The queue the drain runs on. `drizzle/0036` creates it, by the owner. */
export const MAIL_SEND_QUEUE = "mail.send";
/** What an enqueue NOTIFYs on. `pg_notify` is delivered at commit, so a rollback wakes nobody. */
export const MAIL_OUTBOX_CHANNEL = "mail_outbox";
/** Set-equal to `mail_outbox.stream`'s CHECK in `drizzle/0036`. */
export const MAIL_STREAMS = ["transactional", "safety"];
const linkPayload = z.object({ url: z.url() });
export const MAIL_PAYLOADS = {
    "password-reset": linkPayload,
    "email-verification": linkPayload,
    "domain-failing": z.object({
        hostname: z.string().min(1),
        failures: z.number().int().positive(),
        printedSkuCount: z.number().int().nonnegative(),
    }),
    "recall-published": z.object({
        recallId: z.uuid(),
        gtin14: z.string().regex(GTIN14),
        productName: z.string(),
        lots: z.array(z.string()),
    }),
    // The token itself: this row is the one plaintext copy, until `expires_at` or a revoke
    // deletes the invite — Drift §U64.
    invite: z.object({
        token: z.string().regex(INVITE_TOKEN),
        orgName: z.string(),
        inviterName: z.string(),
        role: z.enum(ROLES),
    }),
    "import.completed": z.object({
        importId: z.uuid(),
        filename: z.string(),
        created: z.number().int().nonnegative(),
        updated: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        notApplied: z.number().int().nonnegative(),
    }),
    // Addressed to the operator, not to a tenant: a Growth or Scale crossing changes no state
    // the organisation can see, so this row is the only record of it — Drift §U59.
    "fair-use-crossed": z.object({
        orgId: z.uuid(),
        orgName: z.string(),
        tier: z.enum(TIERS),
        window: z.string().regex(USAGE_WINDOW),
        resolverRequests: z.number().int().nonnegative(),
        fairUsePerMonth: z.number().int().positive(),
    }),
};
export const MAIL_TEMPLATE_STREAMS = {
    "password-reset": "transactional",
    "email-verification": "transactional",
    "domain-failing": "safety",
    "recall-published": "safety",
    // Not a safety message — QP5 keeps that stream's sender reputation shared with nothing.
    invite: "transactional",
    "import.completed": "transactional",
    // An operator notice is not a safety message either. QP5 reserves `safety` for mail that
    // shares reputation with nothing.
    "fair-use-crossed": "transactional",
};
export const MAIL_TEMPLATES = Object.keys(MAIL_PAYLOADS);
export function isMailTemplate(value) {
    return Object.hasOwn(MAIL_PAYLOADS, value);
}
//# sourceMappingURL=mail.js.map